import * as React from "react";

import { Label } from "@impero-com/common/components/html";
import { FormSelect } from "@impero-com/common/components/legacyWidgets";
import * as DV from "@impero-com/common/utils/dataView";
import { t } from "@impero-com/common/utils/i18n";
import { set, assertNever } from "@impero-com/common/utils/obj";
import { Button, Modal } from "@impero-com/ui";
import { Cascader } from "antd";
import type { DefaultOptionType } from "antd/es/cascader";

import { DataViewerGroupingSelector } from "components/dataViewer";

import * as theme from "./theme.css";

class DataViewerSettingsDialog<ITEM> extends React.Component<
    DataViewerSettingsDialogProps<ITEM>,
    DataViewerSettingsDialogState
> {
    constructor(props: DataViewerSettingsDialogProps<ITEM>) {
        super(props);
        this.state = {
            settings: props.settings,
        };
    }
    render() {
        const { extractorGroups, onOk, onCancel } = this.props;
        return (
            <Modal
                variant="content"
                open
                onClose={onCancel}
                title={t("DataViewer_SettingsDialog_Title")}
                footer={
                    <>
                        <Button onClick={onCancel}>{t("Cancel")}</Button>
                        <Button
                            variant="primary"
                            onClick={() => onOk(this.state.settings)}
                        >
                            {t("Apply")}
                        </Button>
                    </>
                }
            >
                <DataViewerGroupingSelector
                    id="dataviewer-settings-grouping"
                    extractorGroups={extractorGroups}
                    groupingExtractorIds={
                        this.state.settings.groupingExtractorIds
                    }
                    onChange={(extractorIds) => {
                        if (extractorIds.length <= 0) {
                            extractorIds =
                                this.props.defaultGroupingExtractorIds || [];
                        }
                        this.setState({
                            settings: set(this.state.settings, {
                                groupingExtractorIds: extractorIds,
                            }),
                        });
                    }}
                />
                <div>
                    <div className={theme.title1}>
                        <Label>{t("DataViewer_SettingsDialog_Content")}</Label>
                    </div>
                    {this.buildAggregatesSelector()}
                </div>
            </Modal>
        );
    }
    private buildAggregatesSelector() {
        const { availableAggregates } = this.props;
        const { settings } = this.state;
        switch (settings.aggregates.type) {
            case "Dimension": {
                return (
                    <DimensionSelector
                        id="dataviewer-settings-dimension"
                        availableAggregates={availableAggregates}
                        aggregates={settings.aggregates}
                        summarizeBy={this.state.settings.summarizeBy}
                        onChange={(aggregates) => {
                            this.setState({
                                settings: set(this.state.settings, {
                                    aggregates,
                                }),
                            });
                        }}
                        onChangeSummarizeBy={(summarizeBy) => {
                            this.setState({
                                settings: set(this.state.settings, {
                                    summarizeBy,
                                }),
                            });
                        }}
                    />
                );
            }
            case "Custom": {
                return null;
            }
            default: {
                return assertNever(settings.aggregates);
            }
        }
    }
}

interface DataViewerSettingsDialogProps<ITEM> {
    extractorGroups: Array<DV.DataExtractorGroup<ITEM>>;
    defaultGroupingExtractorIds?: Array<string>;
    availableAggregates: AggregatesConfiguration<ITEM>;
    settings: DataViewerSettings;
    onOk: (settings: DataViewerSettings) => void;
    onCancel: () => void;
}

interface DataViewerSettingsDialogState {
    settings: DataViewerSettings;
}

interface DataViewerSettings {
    groupingExtractorIds: Array<string>;
    aggregates: AggregatesSpec;
    summarizeBy: DV.SummarizeBy;
}

class DimensionSelector<ITEM> extends React.Component<
    DimensionSelectorProps<ITEM>
> {
    render() {
        const { id, availableAggregates, aggregates, summarizeBy } = this.props;

        const { options, value } = this.buildOptions(
            availableAggregates,
            aggregates
        );

        return (
            <div>
                <div className={theme.title2}>
                    <Label>{t("DataViewer_DimensionSelector_Title")}</Label>
                </div>
                <div>
                    <Cascader
                        id={`${id}-dropdown`}
                        className={theme.cascader}
                        options={options}
                        value={value}
                        showSearch
                        displayRender={renderSelectedLabel}
                        onChange={(value) => {
                            const dimensionId = value?.[value.length - 1];
                            if (typeof dimensionId === "string") {
                                this.selectDimension(dimensionId);
                            }
                        }}
                    />
                </div>
                <div className={theme.title2}>
                    <Label>{t("DataViewer_SummarizeDataBy")}</Label>
                </div>
                <FormSelect
                    id={`${id}-summarize`}
                    options={[
                        {
                            label: t("DataViewer_SummarizeDataBy_None"),
                            value: "NONE",
                        },
                        {
                            label: t("DataViewer_SummarizeDataBy_BottomUp"),
                            value: "BOTTOM_UP",
                        },
                    ]}
                    value={summarizeBy}
                    onChange={(value) =>
                        this.props.onChangeSummarizeBy(value as DV.SummarizeBy)
                    }
                />
            </div>
        );
    }
    private buildOptions(
        availableAggregates: AggregatesConfiguration<ITEM>,
        aggregates: DimensionAggregatesSpec
    ) {
        const options: Array<DefaultOptionType> = [
            {
                value: "standard",
                label: t("Report_Status_Dimension_Standard"),
                children: availableAggregates.dimensions.map((dim) => ({
                    value: dim.id,
                    label: dim.label,
                })),
            },
        ];
        const selected = availableAggregates.dimensions.find(
            (dim) => dim.id === aggregates.dimensionId
        );
        const value = selected ? ["standard", selected.id] : [];
        return { options, value };
    }
    private selectDimension(dimensionId: string) {
        const aggregates: DimensionAggregatesSpec = {
            type: "Dimension",
            dimensionId,
        };
        this.props.onChange(aggregates);
    }
}

// Selected Cascader path rendered as "Group: item", group part muted.
function renderSelectedLabel(labels: Array<string>) {
    if (labels.length < 2) {
        return labels[labels.length - 1] ?? "";
    }
    return (
        <span>
            <span className={theme.selectedGroup}>{labels[0]}:</span>{" "}
            {labels[1]}
        </span>
    );
}

interface DimensionSelectorProps<ITEM> {
    id: string;
    availableAggregates: AggregatesConfiguration<ITEM>;
    aggregates: DimensionAggregatesSpec;
    summarizeBy: DV.SummarizeBy;
    onChange: (aggregates: DimensionAggregatesSpec) => void;
    onChangeSummarizeBy: (summarizeBy: DV.SummarizeBy) => void;
}

interface DimensionAggregatesSpec {
    type: "Dimension";
    dimensionId: string;
}

interface CustomAggregatesSpec {
    type: "Custom";
    aggregateIds: Array<string>;
}

type AggregatesSpec = DimensionAggregatesSpec | CustomAggregatesSpec;

interface AggregatesDimension {
    id: string;
    label: string;
    aggregateIds: Array<string>;
}

interface AggregatesConfiguration<ITEM> {
    aggregates: Array<DV.ExtractorAggregate<ITEM, any>>;
    dimensions: Array<AggregatesDimension>;
}

function buildExtractorAggregates<ITEM>(
    availableAggregates: AggregatesConfiguration<ITEM>,
    settings: DataViewerSettings
): Array<DV.ExtractorAggregate<ITEM, any>> {
    let aggregateIds: Array<string>;
    switch (settings.aggregates.type) {
        case "Dimension": {
            const dimensionId = settings.aggregates.dimensionId;
            const dimension = availableAggregates.dimensions.find(
                (dim) => dim.id === dimensionId
            );
            if (!dimension) {
                console.warn("Dimension " + dimensionId + " not found");
                aggregateIds = [];
            } else {
                aggregateIds = dimension.aggregateIds;
            }
            break;
        }
        case "Custom": {
            aggregateIds = settings.aggregates.aggregateIds;
            break;
        }
        default: {
            return assertNever(settings.aggregates);
        }
    }
    const result = aggregateIds
        .map((aggId) => {
            const agg = availableAggregates.aggregates.find(
                (agg) => agg.aggregateId === aggId
            );
            if (!agg) {
                console.warn(`Aggregate ${aggId} not found`);
            }
            return agg;
        })
        .filter((agg) => (agg ? true : false)) as Array<
        DV.ExtractorAggregate<ITEM, any>
    >;
    return result;
}

export type {
    DataViewerSettingsDialogProps,
    DataViewerSettings,
    AggregatesSpec,
    AggregatesDimension,
    AggregatesConfiguration,
};
export { DataViewerSettingsDialog, buildExtractorAggregates };
